import React, { useState, useEffect, useRef } from "react";
import { Alert, Dialog, CircularProgress, MenuItem, Select, FormControl, InputLabel, TextField, Button } from "@mui/material";
import { X, PlusCircle } from "lucide-react";
import mealsApi from "../../api/meals";
import drinksApi from "../../api/drinks";
import orderApi from "../../api/orders";
import useAuth from "../../hooks/useAuth";
import { formatItemCounts } from "../../utils/orderItemCounts";

interface AdhocOrderModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedDate: Date;
}

const AdhocOrderModal: React.FC<AdhocOrderModalProps> = ({ open, onClose, onSuccess, selectedDate }) => {
  const { selectedKitchenId } = useAuth();
  const [meals, setMeals] = useState<any[]>([]);
  const [drinks, setDrinks] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState("");
  const [mealSelections, setMealSelections] = useState([{ mealId: "", quantity: 1 }]);
  const [drinkSelections, setDrinkSelections] = useState<{ drinkId: string; quantity: number }[]>([]);
  
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    deliveryTime: "05:00 AM - 06:00 AM",
    dietaryPreference: "everything",
    deliveryDetails: {
      address: "",
      flatNumber: "",
      postcode: "",
    }
  });

  useEffect(() => {
    if (open) {
      setError("");
      setMealSelections([{ mealId: "", quantity: 1 }]);
      setDrinkSelections([]);
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const [mealsRes, drinksRes] = await Promise.all([
        mealsApi.getMeals({ page: 1, limit: 100 }),
        drinksApi.getDrinks({ limit: 100 }),
      ]);
      setMeals(mealsRes.meals || []);
      const fetchedDrinks = Array.isArray(drinksRes) ? drinksRes : drinksRes?.drinks || [];
      setDrinks(fetchedDrinks);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Unable to load meals and drinks. Close and reopen to retry.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const allSelections = [...mealSelections, ...drinkSelections];
    const total = allSelections.reduce((sum, item) => sum + item.quantity, 0);
    if (!allSelections.length || total > 100 || allSelections.some(item =>
      !("mealId" in item ? item.mealId : item.drinkId) || !Number.isInteger(item.quantity) || item.quantity < 1)) {
      setError("Select at least one meal or drink, with positive whole-number quantities and at most 100 total items.");
      return;
    }
    setError("");
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await orderApi.createAdhocOrder({
        ...(selectedKitchenId ? { kitchenId: selectedKitchenId } : {}),
        ...formData,
        meals: mealSelections,
        drinks: drinkSelections,
        dietaryPreferences: formData.dietaryPreference,
        deliveryDate: selectedDate
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error creating adhoc order:", error);
      setError(error.response?.data?.message || "Failed to create ad-hoc order");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: '16px' } }}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <PlusCircle className="w-5 h-5 mr-2 text-indigo-600" /> Create Ad-Hoc Order
        </h2>
        <button disabled={submitting} onClick={onClose} className="p-2 text-gray-400 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-6">
        {loadingData ? (
          <div className="flex justify-center py-8"><CircularProgress /></div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert severity="error">{error}</Alert>}
            <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-50 mb-4">
              <p className="text-sm text-indigo-800 font-medium">
                This order will be delivered on <span className="font-bold">{selectedDate.toLocaleDateString()}</span>.
              </p>
            </div>

            <div className="pt-2">
              <p className="text-sm font-bold text-gray-700 mb-3">Customer Details</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <TextField
                  label="Name"
                  size="small"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <TextField
                  label="Phone Number"
                  size="small"
                  fullWidth
                  required
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-bold text-gray-700">Meals</p>
              {mealSelections.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <FormControl fullWidth size="small">
                    <InputLabel id={`adhoc-meal-${index}`}>Meal {index + 1}</InputLabel>
                    <Select labelId={`adhoc-meal-${index}`} value={item.mealId} label={`Meal ${index + 1}`} required
                      onChange={e => setMealSelections(items => items.map((row, i) => i === index ? { ...row, mealId: e.target.value } : row))}>
                      {meals.map(meal => <MenuItem key={meal._id} value={meal._id}>{meal.name} (S/N: {meal.serialNumber})</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label={`Meal ${index + 1} quantity`} type="number" size="small" required value={item.quantity || ""}
                    inputProps={{ min: 1, max: 100, step: 1 }} sx={{ minWidth: 100, width: 120 }}
                    onChange={e => setMealSelections(items => items.map((row, i) => i === index ? { ...row, quantity: Number(e.target.value) } : row))} />
                  <Button aria-label={`Remove meal ${index + 1}`} onClick={() => setMealSelections(items => items.filter((_, i) => i !== index))}>Remove</Button>
                </div>
              ))}
              <Button onClick={() => setMealSelections(items => [...items, { mealId: "", quantity: 1 }])}>Add meal</Button>
              <p className="text-sm font-bold text-gray-700">Drinks</p>
              {drinkSelections.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <FormControl fullWidth size="small">
                    <InputLabel id={`adhoc-drink-${index}`}>Drink {index + 1}</InputLabel>
                    <Select labelId={`adhoc-drink-${index}`} value={item.drinkId} label={`Drink ${index + 1}`} required
                      onChange={e => setDrinkSelections(items => items.map((row, i) => i === index ? { ...row, drinkId: e.target.value } : row))}>
                      {drinks.filter(drink => drink.isActive !== false).map(drink => <MenuItem key={drink._id} value={drink._id}>{drink.name} ({drink.type === "coffee" ? "Coffee" : "Blend"})</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label={`Drink ${index + 1} quantity`} type="number" size="small" required value={item.quantity || ""}
                    inputProps={{ min: 1, max: 100, step: 1 }} sx={{ minWidth: 100, width: 120 }}
                    onChange={e => setDrinkSelections(items => items.map((row, i) => i === index ? { ...row, quantity: Number(e.target.value) } : row))} />
                  <Button aria-label={`Remove drink ${index + 1}`} onClick={() => setDrinkSelections(items => items.filter((_, i) => i !== index))}>Remove</Button>
                </div>
              ))}
              <Button onClick={() => setDrinkSelections(items => [...items, { drinkId: "", quantity: 1 }])}>Add drink</Button>
              <p className="text-sm text-gray-600" aria-live="polite">
                {formatItemCounts(mealSelections.reduce((sum, item) => sum + item.quantity, 0), drinkSelections.reduce((sum, item) => sum + item.quantity, 0))}
              </p>
              <p className="text-xs text-gray-500">Choose quantities independently. Meals only, drinks only, and mixed orders are supported.</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <FormControl fullWidth size="small">
                <InputLabel>Delivery Slot</InputLabel>
                <Select
                  value={formData.deliveryTime}
                  label="Delivery Slot"
                  onChange={(e) => setFormData({ ...formData, deliveryTime: e.target.value })}
                >
                  <MenuItem value="05:00 AM - 06:00 AM">05:00 AM - 06:00 AM</MenuItem>
                  <MenuItem value="06:00 AM - 07:00 AM">06:00 AM - 07:00 AM</MenuItem>
                  <MenuItem value="07:00 AM - 08:00 AM">07:00 AM - 08:00 AM</MenuItem>
                  <MenuItem value="08:00 AM - 09:00 AM">08:00 AM - 09:00 AM</MenuItem>
                  <MenuItem value="09:00 AM - 10:00 AM">09:00 AM - 10:00 AM</MenuItem>
                </Select>
              </FormControl>
              
            </div>

            <FormControl fullWidth size="small" className="mb-4">
              <InputLabel>Dietary Preference</InputLabel>
              <Select
                value={formData.dietaryPreference}
                label="Dietary Preference"
                onChange={(e) => setFormData({ ...formData, dietaryPreference: e.target.value })}
              >
                <MenuItem value="everything">Everything</MenuItem>
                <MenuItem value="lactose-free">Lactose-Free</MenuItem>
                <MenuItem value="dairy-free">Dairy-Free</MenuItem>
                <MenuItem value="gluten-free">Gluten-Free</MenuItem>
              </Select>
            </FormControl>

            <div className="pt-2 border-t border-gray-100">
              <p className="text-sm font-bold text-gray-700 mb-3">Delivery Address</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <TextField
                  label="Flat / House No"
                  size="small"
                  fullWidth
                  value={formData.deliveryDetails.flatNumber}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    deliveryDetails: { ...formData.deliveryDetails, flatNumber: e.target.value } 
                  })}
                />
                <TextField
                  label="Postcode"
                  size="small"
                  fullWidth
                  value={formData.deliveryDetails.postcode}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    deliveryDetails: { ...formData.deliveryDetails, postcode: e.target.value } 
                  })}
                />
              </div>
              <TextField
                label="Full Address (Street / Building)"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={formData.deliveryDetails.address}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  deliveryDetails: { ...formData.deliveryDetails, address: e.target.value } 
                })}
              />
            </div>

            <div className="flex justify-end pt-4 mt-6 border-t border-gray-100">
              <Button disabled={submitting} onClick={onClose} color="inherit" className="mr-2">Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={submitting}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {submitting ? <CircularProgress size={24} /> : "Create Ad-Hoc Order"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Dialog>
  );
};

export default AdhocOrderModal;
